import { Routes } from '@common/constants';
import { ListPage } from '@features/List';
import { useRoomPageSettings } from '@features/rooms';
import { usePackagesSpaAvailability } from '@features/spa/hooks/useGetPackagesSpaAvailability';
import { useRatesErrorMessage } from '@common/hooks/useRatesErrorMessage';
import { useModuleStatuses } from '@features/properties';
import { useRatePageSettings, useRates } from '@features/rates';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useTranslations } from '@lang';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';
import { Moment } from 'moment';
import { FC, useEffect } from 'react';
import { useRouter } from 'shared-routing';
import { PromoRateCode } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

type PackagesProps = {
  journeyParams: ParsedJourneyParams;
  changeJourneyParam: (paramName: string, value?: string | Moment | number | PromoRateCode) => void;
  property: Property;
  language: string;
};

export const Packages: FC<PackagesProps> = ({
  journeyParams,
  changeJourneyParam,
  property,
  language,
}) => {
  const router = useRouter();
  const isFeatureOn = useFeatureIsOn('packages');
  const { t } = useTranslations();
  const { content: roomsPageContent, isFetching: isFetchingRoomsContent } = useRoomPageSettings(
    language,
    t('common.rate')
  );

  const { content: ratesPageContent, isFetching: isFetchingRatesContent } = useRatePageSettings(
    language,
    t('common.rate')
  );

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();
  const acceptPromoRateCode = isRoomsEnabled;

  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const promoRateCode =
    typeof router.query.promoRateCode === 'string'
      ? router.query.promoRateCode
      : journeyParams.promoRateCode;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const { rates, isFetching, error } = useRates({
    fromDate,
    toDate,
    adults,
    children,
    promoRateCode,
    childAges,
    rooms,
  });

  const { data: availability, isFetching: isFetchingAvailability } = usePackagesSpaAvailability(
    property,
    rates,
    journeyParams
  );

  const rateListItems = rates
    .filter((rate) => !rate.unAvailable)
    .map((rate) => {
      const rateAvailability = availability ? availability[rate.id] : undefined;
      const isUnavailable = rateAvailability?.isUnavailable ?? false;

      return {
        ...rate,
        unAvailable: isUnavailable,
        linkRoute: Routes.RATE_DETAIL,
        linkParams: {
          id: rate.id,
          fromDate,
          toDate,
          adults,
          children,
          promoRateCode,
        },
      };
    });
  const errorMessage = useRatesErrorMessage(error);

  const onChangeJourneyParams = (params) => {
    changeJourneyParam('fromDate', params.fromDate);
    changeJourneyParam('spaDate', params.fromDate);
    changeJourneyParam('toDate', params.toDate);
    changeJourneyParam(
      'adults',
      (params.rooms || []).reduce((acc, room) => acc + room.adults, 0) ?? params.adults
    );
    changeJourneyParam(
      'children',
      (params.rooms || []).reduce((acc, room) => acc + room.children, 0) ?? params.children
    );
    changeJourneyParam('promoRateCode', params.promoRateCode);
    changeJourneyParam('childAges', params.childAges);
    changeJourneyParam('rooms', params.rooms);
    const { promoRateCode, childAges: _childAges, rooms: _rooms, ...otherParams } = params;
    const { rateCode } = router.query;
    router.push({
      pathname: Routes.RATE_LIST,
      query: {
        ...otherParams,
        ...(promoRateCode ? { promoRateCode } : undefined),
        ...(rateCode ? { rateCode } : undefined),
      } as any,
    });
  };

  useEffect(() => {
    if (!isFeatureOn) {
      router.push({
        pathname: Routes.HOME,
      });
    }
  }, [isFeatureOn, router]);

  if (!isFeatureOn) {
    return <></>;
  }

  return (
    <ListPage
      journeyParams={journeyParams}
      onChangeJourneyParams={onChangeJourneyParams}
      title={ratesPageContent.title ?? roomsPageContent.title}
      description={ratesPageContent.description ?? roomsPageContent.description}
      items={rateListItems}
      isFetching={
        isFetching || isFetchingRoomsContent || isFetchingRatesContent || isFetchingAvailability
      }
      errorMessage={errorMessage}
      acceptPromoRateCode={acceptPromoRateCode}
    />
  );
};
